import View from '@play-co/timestep-core/lib/ui/View';
import i18n from 'src/lib/i18n/i18n';
import uiConfig from 'src/lib/ui/config';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import { Feature } from 'src/replicant/analytics';
import { ImageScaleView, ImageSliceView } from '@play-co/timestep-core/ui';
import sounds from 'src/lib/sounds';
import PopupBasicLazy from '../components/popups/PopupBasicLazy';
import { AssetGroup } from '@play-co/timestep-core';
import { getRandomStaticShareInviteCreative } from 'src/creatives/share';
import { getCreativeText } from 'src/creatives/text';
import {
  generateInviteURL,
  sendInviteURL,
  sendInviteShareTargetPicker,
  isShareTargetPickerAvailable,
} from 'src/lib/sharing';
import { track } from 'src/lib/analytics/utils';
import { withLoading } from 'src/lib/utils';
import { openPopupPromise } from 'src/lib/popups/popupOpenClose';
import features from 'src/replicant/ruleset/features';
import { user } from 'src/StateObserver';

type ShareData = {
  inviteUrl: string;
  description: string;
};

/**
 * Show a generic prompt for inviting friends with an invite button that shares an invite link via native share dialog
 */
export default class PopupGenericInviteFriends extends PopupBasicLazy {
  /**
   * Green invite button that triggers native share
   */
  private inviteButton: ButtonScaleViewWithText;

  /** Whether the player has initiated an invite while this popup is open. */
  private hasInvited = false;

  /**
   * Build UI
   */
  constructor(opts: { superview: View; close: (didInvite: boolean) => void }) {
    const { width } = uiConfig.popups.buruburu;

    super({
      superview: opts.superview,
      close: () => opts.close(this.hasInvited),
      zIndex: 10000,
      width,
      height: 641,
      darkerBg: false,
      tracking: {
        feature: Feature.SHARE_INVITE._,
        $subFeature: Feature.SHARE_INVITE.POPUP,
      },
    });

    this.message.updateOpts({
      size: 28,
      x: width * 0.5,
      y: 400,
      centerOnOrigin: true,
    });

    // Top separator
    new ImageSliceView({
      superview: this.box,
      x: width * 0.5,
      y: 90,
      width: 284,
      height: 5,
      image: 'assets/ui/popups/panel_underline.png',
      slices: {
        top: 2,
        bot: 2,
        left: 2,
        right: 2,
      },
      centerOnOrigin: true,
    });

    // Decoration graphic
    new ImageScaleView({
      superview: this.box,
      image: 'assets/ui/friends/invite_generic.png',
      x: width * 0.5,
      y: 245,
      width: 515,
      height: 289,
      centerOnOrigin: true,
    });

    // Bottom separator
    new ImageSliceView({
      superview: this.box,
      x: width * 0.5,
      y: 495,
      width: 284,
      height: 5,
      image: 'assets/ui/popups/panel_underline.png',
      slices: {
        top: 2,
        bot: 2,
        left: 2,
        right: 2,
      },
      centerOnOrigin: true,
    });

    this.inviteButton = new ButtonScaleViewWithText({
      superview: this.box,
      ...uiConfig.buttons.secondary,
      width: 238,
      height: 87,
      x: width * 0.5,
      y: 565,
      fontSize: 40,
      font: 'Body',
      localeText: () => i18n('social.invite.referral.cta'),
      tracking: {
        feature: Feature.SHARE_INVITE._,
        $subFeature: Feature.SHARE_INVITE.POPUP_CTA,
      },
      centerOnOrigin: true,
    });
  }

  /**
   * Preload assets before showing the popup
   */
  public getAssetsGroup() {
    return new AssetGroup(
      AssetGroup.constructURLs(['assets/ui/friends/invite_generic.png']),
    );
  }

  /**
   * On popup show
   */
  public init() {
    super.init({
      title: i18n('social.invite.generic.title'),
      message: i18n('social.invite.generic.message'),
    });

    let linkGeneration = this.generateInviteURL();

    // Make sure that button is on, just in case
    this.inviteButton.setDisabled(false);

    // Setup button click
    this.inviteButton.onClick = async () => {
      // Play click sound effect
      sounds.sfx.play('ok');

      // Disable button after click to prevent multiple clicks
      this.inviteButton.setDisabled(true);

      if (isShareTargetPickerAvailable()) {
        // Trigger LINE share target picker functionality
        await withLoading(sendInviteShareTargetPicker, {
          feature: Feature.INVITE._,
          subFeature: Feature.INVITE.LINE_SHARE,
        });
        track('InviteSend', { method: 'line_share' });
      } else {
        // Wait for link generation to finish
        // In 99% it will be already finished
        const data = await withLoading(() => linkGeneration, {
          feature: Feature.INVITE._,
          subFeature: Feature.INVITE.LINE_SHARE,
        });

        // Handle generation failure
        if (!data) {
          // Enable button back to try again
          this.inviteButton.setDisabled(false);
          // Restart link generation
          linkGeneration = this.generateInviteURL();
          // Show error popup on failure
          await openPopupPromise('popupError', {
            message: i18n(`error.general.tryAgain`),
          });
          return;
        }

        // Send invite and close popup
        await this.sendInvite(data);
      }

      // Remember the action so the eventual popup close reports resolution.
      this.hasInvited = true;

      // Re-enable the button while the popup remains open.
      this.inviteButton.setDisabled(false);
    };
  }

  /**
   * Generates an invite URL with creative assets and tracking data.
   * @returns Promise that resolves to share data containing the invite URL, or null if generation fails.
   */
  private async generateInviteURL(): Promise<ShareData | null> {
    // If user has Add Friends V2 enabled, use add_friends_v2 subfeature
    // Otherwise, use the default invite subfeature
    const $subFeature = features(user()).addFriendsV2
      ? Feature.INVITE.NATIVE_SHARE
      : Feature.INVITE.LINE_SHARE;

    return generateInviteURL({
      creativeAsset: await getRandomStaticShareInviteCreative(),
      creativeText: getCreativeText('invite'),
      data: {
        feature: Feature.INVITE._,
        $subFeature,
        originFeature: Feature.INVITE._,
        originSubFeature: $subFeature,
      },
    });
  }

  /**
   * Sends an invite URL via native share and tracks the action.
   * @param data Share data containing the invite URL
   * @returns Promise that resolves when invite is sent and tracked
   */
  private async sendInvite(data: ShareData): Promise<void> {
    // Send invite URL and retrieve share method used
    const shareMethod = await sendInviteURL({
      ...data,
      fallbackPopupOpts: {
        title: i18n('social.invite.referral.copiedUrl'),
        message: i18n('social.invite.referral.copiedUrlMessage'),
      },
    });

    // Track invite URL sent event with share method
    track('SendInviteURL', {
      url: data.inviteUrl,
      shareMethod,
    });

    track('InviteSend', { method: 'native_share' });
  }
}
